const _ = require('lodash')
const {DATA_TYPES, NAMESPACE_MAPPING} = require('@wix/document-services-json-schemas/dist/constants.json')
const {
    parseStylableStyleItems,
    migrateProperties,
    migrateStyleItemsSkin,
    createPropertiesSource,
    extractComponentStyleItems,
    mergeStylesByBreakpoints,
    migrateComponentSkin,
    initiateStyleObject,
    getAllComponentsOfType,
    getAllRemoteStructureComponentsOfType,
    updateStyleItem,
    migratedStyleToStyleProperties,
    updateOldComponentStructure
} = require('./index')
const {refArray} = require('../../helpers/dataUtils')

const BLOCKS_APP_DEF_ID = '46b2ad43-5720-41d2-8436-2058979cb53f'
const REF_COMPONENT_TYPE = 'wysiwyg.viewer.components.RefComponent'
const PRODUCT_PAGE_APP_DEF_ID = 'a0c68605-c2e7-4c8d-9ea1-767f9770e087'

/**
 * @param {object} props
 * @param {object} props.styles
 * @param {import('./index').SelectorsMapping} props.selectorsMappings
 * @param {string} props.newSkin
 * @param {Array<string>} props.systemStyleIDs
 * @param {boolean} props.useCssColor
 * @param {boolean} props.withBorderRadiusAsOneParam
 * @returns {Array<import('./index').ParsedStyleItem>}
 */
const migrateSystemStyles = ({
    styles,
    selectorsMappings,
    newSkin,
    systemStyleIDs,
    useCssColor,
    withBorderRadiusAsOneParam
}) => {
    const systemStyles = systemStyleIDs
        .map(id => styles[id])
        .filter(item => _.get(item, 'style.properties.$st-css') !== undefined) // not all of supported systemStyles will be defined and be valid styleItems
    const parsedStyleItems = parseStylableStyleItems(systemStyles, {
        selectorsMappings
    })

    // SystemStyles undergo same transformations as ComponentStyles, except for breakpoint: they do not need to be merged by breakpoint
    const migratedStyleItems = _.flow([
        styleItems => migrateProperties({useCssColor, withBorderRadiusAsOneParam})(styleItems),
        styleItems => migrateStyleItemsSkin(styleItems, newSkin),
        createPropertiesSource
    ])(parsedStyleItems)
    return migratedStyleItems
}
/**
 *
 * @param {object} props
 * @param {object} props.styles
 * @param {object} props.variants
 * @param {Array<import('./index').ComponentStructure>} props.components
 * @param {object} props.mobileOnlyComponentsIds
 * @param {import('./index').SelectorsMapping} props.selectorsMappings
 * @param {string} props.newSkin
 * @param {boolean} props.useCssColor
 * @param {boolean} props.withBorderRadiusAsOneParam
 * @returns {Array<{migratedComponent: import('./index').ComponentStructure, migratedStyleItems: Array<import('./index').ParsedStyleItem>, shouldMigrate: boolean}>}
 */
const getMigratedComponentAndStyleItems = ({
    styles,
    variants,
    components,
    mobileOnlyComponentsIds,
    selectorsMappings,
    newSkin,
    useCssColor,
    withBorderRadiusAsOneParam
}) =>
    components.map(component => {
        const compStyleItems = extractComponentStyleItems({
            component,
            styles,
            variants,
            mobileOnlyComponentsIds
        })

        if (!compStyleItems.length) {
            return {
                migratedComponent: component,
                migratedStyleItems: [],
                shouldMigrate: false
            }
        }

        const parsedStyleItems = parseStylableStyleItems(compStyleItems, {
            selectorsMappings
        })

        const migratedStyleItems = _.flow([
            mergeStylesByBreakpoints,
            styleItems => migrateProperties({useCssColor, withBorderRadiusAsOneParam})(styleItems),
            styleItems => migrateStyleItemsSkin(styleItems, newSkin),
            createPropertiesSource
        ])(parsedStyleItems)

        const migratedComponent = migrateComponentSkin(component, newSkin)

        return {
            migratedComponent,
            migratedStyleItems,
            shouldMigrate: true
        }
    })

/**
 * @param {object} data
 * @param {object} clientSpecMap
 * @param {object} magicObject
 * @param {Function} magicObject.isExperimentOpen
 * @returns {boolean}
 */
const shouldIgnorePageEnvironment = (data, clientSpecMap, magicObject) => {
    if (magicObject.isExperimentOpen('dm_migrateBlocksWidgetsToNonStylable')) {
        return false
    }

    return (
        _.some(clientSpecMap, {appDefinitionId: BLOCKS_APP_DEF_ID}) &&
        _.some(data[NAMESPACE_MAPPING.variants], ({type}) => type === 'Preset')
    )
}

/**
 * @param {object} props
 * @param {object} props.structure
 * @param {object} props.data
 * @param {string} props.compType
 * @param {{getUniqueId: any}} props.uniqueIdGenerator
 * @param {object} props.clientSpecMap
 * @param {import('./index').SelectorsMapping} props.selectorsMappings
 * @param {string} props.newSkin
 * @param {boolean} [props.isMasterPage]
 * @param {Array<string>} [props.systemStyleIDs]
 * @param {boolean} [props.useCssColor]
 * @param {boolean} [props.withBorderRadiusAsOneParam]
 * @param {import('./index').PostStyleMigrationCallback} [props.postStyleMigrationCallback]
 * @param {object} props.magicObject
 * @returns {void}
 */
function migrateStylableComponentsByCompType({
    structure,
    data,
    compType,
    uniqueIdGenerator,
    clientSpecMap,
    selectorsMappings,
    newSkin,
    magicObject,
    isMasterPage = false,
    systemStyleIDs = [],
    useCssColor = false,
    withBorderRadiusAsOneParam = false,
    postStyleMigrationCallback
}) {
    if (shouldIgnorePageEnvironment(data, clientSpecMap, magicObject)) {
        return
    }

    const styles = data[NAMESPACE_MAPPING.style]
    const variants = data[NAMESPACE_MAPPING.variants]
    const documentData = data[NAMESPACE_MAPPING.data]

    // update page data with style object if not exists
    initiateStyleObject(data)
    const {components, mobileOnlyComponentsIds, mobileComps} = getAllComponentsOfType(structure, compType)
    const componentsToMigrate = Object.values(components)

    const shouldMigrateBlocksStyleOverrides = magicObject.isExperimentOpen('dm_migrateBlocksWidgetsStylableOverrides')
    if (shouldMigrateBlocksStyleOverrides) {
        const {components: refComponents} = getAllComponentsOfType(structure, REF_COMPONENT_TYPE)

        // At this point we only want to migrate Stores' Product Page components to non stylable, as we have other gaps for Blocks Migration.
        const refComponentsToMigrate = Object.values(refComponents).filter(refComp => {
            const {dataQuery} = refComp
            if (_.get(documentData[dataQuery.replace('#', '')], 'appDefinitionId') === PRODUCT_PAGE_APP_DEF_ID) {
                return true
            }
            return false
        })

        const remoteStructureComponents = getAllRemoteStructureComponentsOfType({
            compType,
            refComponents: refComponentsToMigrate,
            styles
        })
        componentsToMigrate.push(...remoteStructureComponents)
    }

    const getNewStyleId = () => uniqueIdGenerator.getUniqueId(DATA_TYPES.theme, '-')

    const migratedComponentsAndStyleItems = getMigratedComponentAndStyleItems({
        styles,
        variants,
        components: componentsToMigrate,
        mobileOnlyComponentsIds,
        selectorsMappings,
        newSkin,
        useCssColor,
        withBorderRadiusAsOneParam
    })

    migratedComponentsAndStyleItems.forEach(({migratedComponent, migratedStyleItems, shouldMigrate}) => {
        if (!shouldMigrate) {
            return
        }

        const {styleId} = migratedComponent
        const compHasRefArray = refArray.isRefArray(styles[styleId])

        if (compHasRefArray) {
            migratedStyleItems.forEach(migratedStyleItem => {
                updateStyleItem(styles, {
                    ...migratedStyleItem,
                    style: migratedStyleToStyleProperties(migratedStyleItem, {
                        postStyleMigrationCallback
                    })
                })
            })
        } else {
            updateOldComponentStructure({
                styles,
                migratedComponent,
                getNewStyleId,
                compStyleItem: migratedStyleItems[0],
                isMobileOnlyComp: _.includes(mobileOnlyComponentsIds, migratedComponent.id),
                postStyleMigrationCallback
            })
        }
        if (!migratedComponent.isFromRemoteStructure) {
            Object.assign(components[migratedComponent.id], migratedComponent)
        }
        const correspondingMobileComp = mobileComps[migratedComponent.id]
        if (correspondingMobileComp) {
            Object.assign(mobileComps[migratedComponent.id], {
                ...correspondingMobileComp,
                styleId: migratedComponent.styleId,
                skin: migratedComponent.skin
            })
        }
    })
    if (isMasterPage) {
        const systemStyles = migrateSystemStyles({
            styles,
            selectorsMappings,
            newSkin,
            systemStyleIDs,
            useCssColor,
            withBorderRadiusAsOneParam
        })
        systemStyles.forEach(styleItem => {
            updateStyleItem(styles, {
                ...styleItem,
                style: migratedStyleToStyleProperties(styleItem, {postStyleMigrationCallback})
            })
        })
    }
}

module.exports = {
    migrateStylableComponentsByCompType
}
